<script lang="ts" setup>
  import { UserIcon, BuildingIcon, LandmarkIcon, ChevronRightIcon } from 'lucide-vue-next'
  import { computed, provide } from 'vue'
  import { EntityTypeEnum, RelationshipTypeEnum } from '@/types/generated'
  import { useTranslation } from '@/composables/useTranslation'
  import { useEntityCard } from '@/composables/useEntityCard'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import EntityCard from '@/components/common/EntityCard.vue'
  const { t } = useTranslation({
    en: {
      Participant: 'Party',
      Participants: 'Parties',
      ViewAllParticipants: 'View all parties',
      UnknownRole: 'Unknown Role'
    },
    de: {
      Participant: 'Beteiligter',
      Participants: 'Beteiligte',
      ViewAllParticipants: 'Alle Beteiligte ansehen',
      UnknownRole: 'Unbekannte Rolle'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed(() => caseStore.caseRecord)

  const router = useRouter()
  import { entitiesDE } from '@/i18n/entity'
  import { useRouter } from 'vue-router'
  const ROLE_MAPPING = entitiesDE

  const formatEntityName = (entity: any) => {
    if (entity.entity_type === EntityTypeEnum.NATURAL_PERSON) {
      const name = `${entity.first_name || ''} ${entity.last_name || ''}`.trim()
      return name || null
    }
    if (entity.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      const name = `${entity.company_name || ''} ${entity.company_legal_form || ''}`.trim()
      return name || null
    }

    if (entity.entity_type === EntityTypeEnum.INSTITUTION) {
      const name = `${entity.institution_name || ''}`.trim()
      return name || null
    }
    return entity.company_name || null
  }

  const getEntityIcon = (entityType: string) => {
    if (entityType === EntityTypeEnum.NATURAL_PERSON) {
      return UserIcon
    }
    if (entityType === EntityTypeEnum.LEGAL_PERSON) {
      return BuildingIcon
    }
    return LandmarkIcon
  }

  const { showCard } = useEntityCard()

  const handleEntityClick = (entity: any, element: HTMLElement) => {
    const entityWithName = {
      ...entity,
      name: formatEntityName(entity)
    }
    showCard(entityWithName, element)
  }

  const viewAllData = () => {
    if (caseStore.caseRecord?.uuid) {
      router.push(`/app/cases/${caseStore.caseRecord.uuid}/entities`)
    }
  }

  provide('caseData', caseRecord)
</script>

<template>
  <div
    class="rounded-xl border border-gray-200 bg-white dark:border-neutral-800 dark:bg-neutral-900"
  >
    <div class="divide-y divide-gray-200 dark:divide-neutral-800">
      <template
        v-for="entity in caseRecord?.data?.entities"
        :key="entity.id"
      >
        <div
          class="flex cursor-pointer items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-neutral-800"
          @click="handleEntityClick(entity.entity, $event.currentTarget)"
        >
          <div class="flex h-8 w-8 shrink-0 items-center justify-center">
            <component
              :is="getEntityIcon(entity.entity.entity_type)"
              class="h-5 w-5 text-gray-500 dark:text-neutral-400"
            />
          </div>
          <div class="flex min-w-0 flex-col">
            <div class="text-base font-medium">
              {{ formatEntityName(entity.entity) || '-' }}
            </div>
            <div class="text-xs text-gray-500 dark:text-neutral-400">
              {{ ROLE_MAPPING[entity.relationships[0]?.relationship_type] || t('UnknownRole') }}
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div class="items-left mt-4 flex justify-end">
    <a
      href="#"
      class="inline-flex items-center text-sm font-medium text-purple-600 underline hover:text-purple-800"
      @click.prevent="viewAllData"
    >
      <span>{{ t('ViewAllParticipants') }}</span>
      <ChevronRightIcon class="ml-1 h-4 w-4" />
    </a>
  </div>
  <EntityCard />
</template>
